import React from 'react';
import cx from 'classnames';
import { components } from 'react-select';
import { RefreshIndicator } from '../../../mui/RefreshIndicator';
import Constants from '../../../../../Constants';
import {
  TRANSFORM_RULES_PROTO_ATTR,
  TRANSFORM_RULES_PROTO_FUNC,
  TRANSFORM_RULES_PROTO_NUM,
  TRANSFORM_RULES_PROTO_STR,
  TRANSFORM_RULES_PROTO_SYS_ATTR,
  TransformParameterTypes,
} from 'js/constants/schemaEditor';
import { Checkbox, InputBase, Stack, styled } from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import DateUtils from '../../../../../lib/DateUtils';
import has from 'lodash/has';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import toNumber from 'lodash/toNumber';
import indexOf from 'lodash/indexOf';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import forEach from 'lodash/forEach';
import some from 'lodash/some';
import find from 'lodash/find';
import filter from 'lodash/filter';
import isNil from 'lodash/isNil';
import keys from 'lodash/keys';
import reduce from 'lodash/reduce';
import intersection from 'lodash/intersection';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from '../../../rselect/ReactSelectUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import { inputStyles } from '../../../../recipeBuilder/styles';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import TransformView from './TransformView';
import LoadableMonacoEditor from '../../../editors/monaco/LoadableMonacoEditor';
import { TextField } from 'new/elements/TextField';
import TransformParametersDotList from 'js/components/elements/schema-editor/schema-inspector/transforms/TransformParametersDotList';

import s from './TransformParameters.module.scss';

const styles = {
  label: {
    height: 18,
    lineHeight: '18px',
    fontSize: 12,
    marginLeft: 2,
  },
  textFieldView: {
    display: 'flex',
    width: '100%',
    paddingLeft: 4,
  },
  monacoFieldView: {
    width: '100%',
    marginLeft: 4,
  },
  monacoCodeView: {
    width: '100%',
    height: 220,
    display: 'flex',
    marginTop: 8,
  },
};

const StyledInput = styled(InputBase)(() => ({
  ...inputStyles.root,
  '.MuiInputBase-input': inputStyles.input,
}));

const ReactSelectOverrides = {
  menuPortal: (base) => ({ ...base, fontSize: 12 }),
};

const AttributeOption = (props) => {
  const { data } = props;
  const { __name, __description, label } = data;

  return (
    <components.Option {...props}>
      <div style={{ paddingLeft: 10 }}>
        <div style={{ fontSize: 'var(--font-size-base-sm)', fontWeight: 300 }}>
          {__name || label || ''}
        </div>
        {__description && (
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 300, opacity: 0.6 }}>
            {__description || ''}
          </div>
        )}
      </div>
    </components.Option>
  );
};

const MetaDataAttributeOption = (props) => {
  const { data } = props;
  const { label, description, example } = data;

  return (
    <components.Option {...props}>
      <div style={{ paddingLeft: 10 }}>
        <div style={{ fontSize: 'var(--font-size-base-sm)', fontWeight: 300 }}>{label || ''}</div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 300,
            opacity: 0.6,
            lineHeight: 'var(--line-height-sm)',
            paddingBottom: 'var(--spacing-md)',
          }}
        >
          {description || ''}
          <br />
          Ex: {example || ''}
        </div>
      </div>
    </components.Option>
  );
};

const _getMetaDataSelectionItem = (metaDataInfo, metaDataName) => {
  const { description, example } = metaDataInfo;
  return {
    isSectionHeader: false,
    label: metaDataName,
    value: metaDataName,
    isMetaData: true,
    description,
    example,
  };
};

const _getAttributeSelectionItem = (attrInfo) => {
  const { __name, __txName, __description } = attrInfo;
  return {
    isSectionHeader: false,
    label: __name,
    value: __txName,
    isMetaData: false,
    __name,
    __txName,
    __description,
  };
};

const getCurrentParamValue = (fieldInfo) => {
  let currentValue = null;
  let currentType = null;

  if (!fieldInfo) {
    return { currentType, currentValue };
  }

  if (!has(fieldInfo, 'type')) {
    return { currentType, currentValue };
  }

  currentType = fieldInfo.type;

  if (
    currentType === TransformParameterTypes.ATTR_REF ||
    currentType === TransformParameterTypes.SYS_ATTR_REF ||
    currentType === TransformParameterTypes.FUNCTION
  ) {
    currentValue = fieldInfo.name;
  } else {
    currentValue = fieldInfo.value;
  }
  return { currentType, currentValue };
};

const ALL_SRC_SELECTION_OPTIONS = [
  { label: 'Attribute', value: TransformParameterTypes.ATTR_REF },
  {
    label: 'Metadata',
    value: TransformParameterTypes.SYS_ATTR_REF,
  },
  { label: 'Function', value: TransformParameterTypes.FUNCTION },
];

const ALL_SELECTION_OPTIONS = [
  { label: 'Attribute', value: TransformParameterTypes.ATTR_REF },
  {
    label: 'Metadata',
    value: TransformParameterTypes.SYS_ATTR_REF,
  },
  { label: 'Function', value: TransformParameterTypes.FUNCTION },
  { label: 'String', value: TransformParameterTypes.STRING_INP },
  { label: 'Number', value: TransformParameterTypes.NUMBER_INP },
];

const SRC_SELECTION_OPTIONS = (allowedTypes = null) => {
  if (!allowedTypes) {
    return ALL_SRC_SELECTION_OPTIONS;
  }

  return filter(ALL_SRC_SELECTION_OPTIONS, (op) => {
    return indexOf(allowedTypes, op.value) > -1;
  });
};

const PARAM_SELECTION_OPTIONS = (allowedTypes) => {
  return filter(ALL_SELECTION_OPTIONS, (op) => {
    return indexOf(allowedTypes, op.value) > -1;
  });
};

const ATTR_TX_PARAM_SELECTION_OPTIONS = () => {
  const allowedTypes = [
    TransformParameterTypes.ATTR_REF,
    TransformParameterTypes.SYS_ATTR_REF,
    TransformParameterTypes.STRING_INP,
    TransformParameterTypes.NUMBER_INP,
  ];

  return filter(ALL_SELECTION_OPTIONS, (op) => {
    return indexOf(allowedTypes, op.value) > -1;
  });
};

class TransformParameters extends React.Component {
  constructor(props) {
    super(props);

    this._getAttributesSelectionOptions = this._getAttributesSelectionOptions.bind(this);
    this._getAttributesFilterOptions = this._getAttributesFilterOptions.bind(this);
    this._onAttributeChanged = this._onAttributeChanged.bind(this);

    this._onSrcTypeChanged = this._onSrcTypeChanged.bind(this);
    this._handleSrcTransformChanged = this._handleSrcTransformChanged.bind(this);
    this._renderSrcAttrSelector = this._renderSrcAttrSelector.bind(this);
    this._renderParamsList = this._renderParamsList.bind(this);
    this._renderParamView = this._renderParamView.bind(this);
    this._renderAttrTxParamsView = this._renderAttrTxParamsView.bind(this);
    this._renderInputElement = this._renderInputElement.bind(this);
    this._handleInputChanged = this._handleInputChanged.bind(this);
    this._handleInputCheckboxChanged = this._handleInputCheckboxChanged.bind(this);
    this._handleTransformParamChanged = this._handleTransformParamChanged.bind(this);
    this._handleAddArrayElement = this._handleAddArrayElement.bind(this);
    this._handleRemoveArrayElement = this._handleRemoveArrayElement.bind(this);
  }

  componentDidMount() {
    const { selectedTransform, transformData } = this.props;

    if (
      selectedTransform &&
      selectedTransform.value.startsWith('customAttrTx_') &&
      !transformData.parameters.attrTxId
    ) {
      this._onAttrTxSelectionChanged(selectedTransform.params[0], selectedTransform.attrTxId);
    }
  }

  componentDidUpdate(prevProps) {
    const { selectedTransform } = this.props;

    if (
      selectedTransform &&
      selectedTransform.value.startsWith('customAttrTx_') &&
      !isEqual(selectedTransform, prevProps.selectedTransform)
    ) {
      this._onAttrTxSelectionChanged(selectedTransform.params[0], selectedTransform.attrTxId);
    }
  }

  _onAttributeChanged({ isMetaData, value }) {
    let srcAttr = isMetaData
      ? { ...TRANSFORM_RULES_PROTO_SYS_ATTR, name: value }
      : { ...TRANSFORM_RULES_PROTO_ATTR, name: value };
    this.props.onSrcAttributeChanged(srcAttr);
  }

  _onSrcTypeChanged(selectedSrcType) {
    let newSrcAttrData;
    let newSrcType = getNewSelectionValue(selectedSrcType, true);

    switch (newSrcType) {
      case TransformParameterTypes.FUNCTION:
        newSrcAttrData = TRANSFORM_RULES_PROTO_FUNC;
        break;
      case TransformParameterTypes.SYS_ATTR_REF:
        newSrcAttrData = TRANSFORM_RULES_PROTO_SYS_ATTR;
        break;

      case TransformParameterTypes.ATTR_REF:
      default:
        newSrcAttrData = TRANSFORM_RULES_PROTO_ATTR;
    }

    this.props.onSrcAttributeChanged({
      ...newSrcAttrData,
    });
  }

  _onLookupSelectionChanged(inputField, index, value) {
    let dataMaps = !isEmpty(this.props.dataMaps) ? this.props.dataMaps : null;

    let cbPayload = {
      __fName: inputField.name,
      type: TransformParameterTypes.NUMBER_INP,
    };
    let cbSecondaryKeyPayload = {
      __fName: 'subKey',
      type: TransformParameterTypes.STRING_INP,
    };

    let dmId;
    let dm = value && has(value, 'id') ? find(dataMaps, (m) => m.id === value.id) : null;
    if (isNil(dm)) {
      dm = null;
      dmId = null;
    } else {
      dmId = dm.id;
    }
    let dmSecondaryProp = dm && has(dm, 'secondaryProperties') ? dm.secondaryProperties[0] : null;
    this._handleSelectionMultipleChanged([
      [cbPayload, dmId],
      [cbSecondaryKeyPayload, dmSecondaryProp],
    ]);
  }

  _handleSrcTransformChanged(srcTransformData) {
    const { transformData } = this.props;
    //transformData:{name, parameters:{__srcAttr://}
    let newSrcAttr = {
      ...transformData.parameters.__srcAttr,
      ...srcTransformData,
    };
    let newParams = { ...transformData.parameters, __srcAttr: newSrcAttr };
    let newTransformData = { ...transformData, parameters: newParams };
    this.props.onTransformChanged(newTransformData);
  }

  _onParamTypeChanged(paramName, index, selectedParamType) {
    const { selectedTransform } = this.props;
    let newParamData;
    let newParamType = getNewSelectionValue(selectedParamType, true);

    switch (newParamType) {
      case TransformParameterTypes.FUNCTION:
        newParamData = TRANSFORM_RULES_PROTO_FUNC;
        break;
      case TransformParameterTypes.SYS_ATTR_REF:
        newParamData = TRANSFORM_RULES_PROTO_SYS_ATTR;
        break;

      case TransformParameterTypes.ATTR_REF:
        newParamData = TRANSFORM_RULES_PROTO_ATTR;
        break;

      case TransformParameterTypes.NUMBER_INP:
        newParamData = TRANSFORM_RULES_PROTO_NUM;
        break;
      case TransformParameterTypes.STRING_INP:
      default:
        newParamData = TRANSFORM_RULES_PROTO_STR;
    }
    //if selected transform.params[name] has default of this type, then use default
    if (has(selectedTransform, 'params')) {
      let paramInfo = find(selectedTransform.params, (p) => {
        return p.name === paramName;
      });
      if (paramInfo && has(paramInfo, `default[${newParamType}]`)) {
        newParamData = {
          ...newParamData,
          value: paramInfo.default[newParamType],
        };
      }
    }

    this._handleTransformParamChanged(paramName, { ...newParamData }, index);
  }

  _handleParamsTransformChanged(paramName, index, paramsTransformData) {
    const { transformData } = this.props;
    //transformData:{name, parameters:{__srcAttr://}
    let currentParamsData = transformData.parameters[paramName];
    let newParamsData;

    if (isArray(currentParamsData)) {
      newParamsData = [...currentParamsData];
      newParamsData[index] = paramsTransformData;
    } else {
      newParamsData = {
        ...currentParamsData,
        ...paramsTransformData,
      };
    }

    let newParams = { ...transformData.parameters, [paramName]: newParamsData };
    let newTransformData = { ...transformData, parameters: newParams };

    this.props.onTransformChanged(newTransformData);
  }

  _onParamAttributeChanged(paramName, index, { isMetaData, value }) {
    let newParamData = isMetaData
      ? { ...TRANSFORM_RULES_PROTO_SYS_ATTR, name: value }
      : { ...TRANSFORM_RULES_PROTO_ATTR, name: value };

    this._handleTransformParamChanged(paramName, { ...newParamData }, index);
  }

  _handleInputChanged(fieldName, { type, value }, index) {
    this._handleTransformParamChanged(fieldName, { type, value }, index);
  }

  _handleSelectionChanged({ __fName, type }, index, newSelectedOption) {
    let newSelectionVal = getNewSelectionValue(newSelectedOption, true);

    let payload = { type };
    if (
      type === TransformParameterTypes.ATTR_REF ||
      type === TransformParameterTypes.SYS_ATTR_REF
    ) {
      payload.name = newSelectionVal;
      // payload.__isMetaData = false;
    } else {
      payload.value = newSelectionVal;
    }

    this._handleTransformParamChanged(__fName, payload, index);
  }

  _handleSelectionMultipleChanged(receivedPayload, newSelectionVal) {
    let transformParams = {};
    forEach(receivedPayload, (rp) => {
      let { __fName, type } = rp[0];
      let payload = { type };
      if (
        type === TransformParameterTypes.ATTR_REF ||
        type === TransformParameterTypes.SYS_ATTR_REF
      ) {
        payload.name = rp[1];
        // payload.__isMetaData = false;
      } else {
        payload.value = rp[1];
      }
      transformParams[__fName] = payload;
    });
    this.props.onTransformParamChanged(transformParams);
  }

  _handleInputCheckboxChanged(fieldName, { type, value }, index) {
    this._handleTransformParamChanged(fieldName, { type, value }, index);
  }

  _onAttrTxSelectionChanged(inputField, attrTxId) {
    const { transformData, attrTransforms } = this.props;
    let cbPayload = {
      name: inputField.name,
      type: TransformParameterTypes.NUMBER_INP,
      value: attrTxId,
      argValues: [],
    };

    // reset func param values only if a new transform id is selected.
    if (has(attrTransforms, attrTxId)) {
      let thisTx = attrTransforms[attrTxId];
      if (has(thisTx, 'custom_config.ui.txArgs.items')) {
        cbPayload.argValues = map(thisTx.custom_config.ui.txArgs.items, (item) => ({
          fName: item.name,
          type: TransformParameterTypes.STRING_INP,
          ...TRANSFORM_RULES_PROTO_STR,
        }));
      }
    }

    if (has(transformData, `parameters${inputField.name}`)) {
      let currentTxParam = transformData.parameters[inputField.name];
      if (
        has(currentTxParam, 'value') &&
        currentTxParam.value === attrTxId &&
        has(currentTxParam, 'argValues')
      ) {
        // try not to reset
        cbPayload.argValues = currentTxParam.argValues;
      }
    }

    this.props.onTransformParamChanged({
      [inputField.name]: cbPayload,
    });
  }

  _onAttrTxFuncTypeChanged(inputField = null, funcParamIdx = 0, funcName = null, newSelectionType) {
    const { transformData } = this.props;

    if (has(transformData, `parameters[${inputField.name}]`)) {
      let currentAttrTxPayload = transformData.parameters[inputField.name];
      let newFuncType = getNewSelectionValue(newSelectionType, true);
      let newItem = { ...TRANSFORM_RULES_PROTO_STR };
      if (newFuncType) {
        switch (newFuncType) {
          case TransformParameterTypes.ATTR_REF:
            newItem = { ...TRANSFORM_RULES_PROTO_ATTR };
            break;

          case TransformParameterTypes.SYS_ATTR_REF:
            newItem = { ...TRANSFORM_RULES_PROTO_SYS_ATTR };
            break;

          case TransformParameterTypes.NUMBER_INP:
            newItem = { ...TRANSFORM_RULES_PROTO_NUM };
            break;
          default:
        }
      }
      let thisArgValue = { ...newItem, type: newFuncType, fName: funcName };

      // console.log(currentAttrTxPayload);
      currentAttrTxPayload.argValues[funcParamIdx] = thisArgValue;
      this.props.onTransformParamChanged({
        [inputField.name]: currentAttrTxPayload,
      });
    }
  }

  _onAttrTxFuncValueChanged(
    inputField = null,
    funcParamIdx = 0,
    funcName = null,
    funcParamType,
    newValue,
  ) {
    //TODO: not handling functions here!!
    const { transformData } = this.props;

    let isSelectionType =
      funcParamType === TransformParameterTypes.ATTR_REF ||
      funcParamType === TransformParameterTypes.SYS_ATTR_REF
        ? true
        : false;

    let newFuncValue = isSelectionType ? getNewSelectionValue(newValue, true) : newValue;

    if (has(transformData, `parameters[${inputField.name}]`)) {
      let currentAttrTxPayload = transformData.parameters[inputField.name];

      let newItem = { ...TRANSFORM_RULES_PROTO_STR, value: newFuncValue };
      switch (funcParamType) {
        case TransformParameterTypes.ATTR_REF:
          newItem = { ...TRANSFORM_RULES_PROTO_ATTR, name: newFuncValue };
          break;

        case TransformParameterTypes.SYS_ATTR_REF:
          newItem = { ...TRANSFORM_RULES_PROTO_SYS_ATTR, name: newFuncValue };
          break;

        case TransformParameterTypes.NUMBER_INP:
          newItem = { ...TRANSFORM_RULES_PROTO_NUM, value: newFuncValue };
          break;
        default:
      }
      let thisArgValue = { ...newItem, fName: funcName };

      // console.log(currentAttrTxPayload);
      currentAttrTxPayload.argValues[funcParamIdx] = thisArgValue;
      this.props.onTransformParamChanged({
        [inputField.name]: currentAttrTxPayload,
      });
    }
  }

  _handleTransformParamChanged(name, value, index) {
    const {
      transformData,
      selectedTransform: { params },
    } = this.props;
    const field = find(params, { name });

    if (['array', 'object'].includes(field?.inputElement)) {
      let paramValues = transformData.parameters?.[name];
      paramValues = isArray(paramValues) ? [...paramValues] : [];
      paramValues[index] = value;

      this.props.onTransformParamChanged({
        [name]: paramValues,
      });
    } else {
      this.props.onTransformParamChanged({ [name]: value });
    }
  }

  _handleAddArrayElement(inputField) {
    const { transformData } = this.props;
    const { name, inputElement, typesAllowed } = inputField;

    let paramValues = transformData.parameters?.[name];
    paramValues = isArray(paramValues) ? [...paramValues] : [];
    const newType = typesAllowed[0];
    const newValue = has(inputField, `default[${newType}]`)
      ? `${inputField.default[newType]}`
      : null;
    const newValueElement = {
      type: newType,
      value: newValue,
    };

    if (inputElement === 'object') {
      paramValues.push(
        {
          type: TransformParameterTypes.STRING_INP,
          value: `key${paramValues.length / 2 + 1}`,
        },
        newValueElement,
      );
    } else {
      paramValues.push(newValueElement);
    }

    this.props.onTransformParamChanged({
      [name]: paramValues,
    });
  }

  _handleRemoveArrayElement(inputField, index) {
    const { transformData } = this.props;
    const { name, inputElement } = inputField;

    let paramValues = transformData.parameters?.[name];
    paramValues = isArray(paramValues) ? [...paramValues] : [];
    paramValues.splice(index, inputElement === 'object' ? 2 : 1);

    this.props.onTransformParamChanged({
      [name]: paramValues,
    });
  }

  _renderParamsList() {
    const {
      level,
      transformData,
      selectedTransform: { params },
    } = this.props;

    return map(params, (field, index) => {
      let { required, display_name, name, inputElement } = field;
      let fieldName = display_name && (
        <div style={styles.label}>
          {display_name} {required === true ? ' *' : null}
        </div>
      );

      let fieldInfo = transformData.parameters?.[name];
      let isArrayField = ['array', 'object'].includes(inputElement);
      let paramValues = isArrayField
        ? map(fieldInfo, getCurrentParamValue)
        : [getCurrentParamValue(fieldInfo)];
      let fieldInputContent = map(paramValues, ({ currentType, currentValue }, index) => {
        // show Remove button if this is an array element OR the key of an
        // object entry
        const showRemoveButton =
          inputElement === 'array' || (inputElement === 'object' && index % 2 === 0);
        // hide dot on second line of data feed api payload
        const hideDot = name === 'selectors' && !(index % 2 === 0);

        return (
          <div key={index} style={{ marginRight: isArrayField ? 24 : 0 }}>
            {!hideDot && (
              <div
                aria-label={`level-${level}`}
                className={s.indentation_dots}
                style={{ top: -8 + index * 40, left: -12 }}
              >
                <TransformParametersDotList level={level} className={s.indentation_dots} />
              </div>
            )}

            {this._renderParamView(field, currentType, currentValue, index)}

            {showRemoveButton && (
              <div
                className={s.delete_btn_container}
                style={{
                  top: 24 + index * 40,
                  right: 0,
                }}
              >
                <DeleteIcon onClick={() => this._handleRemoveArrayElement(field, index)} />
              </div>
            )}
          </div>
        );
      });

      return (
        <div key={'grp-' + index} className={s.params_list_container}>
          {fieldName}
          {fieldInputContent}
          {isArrayField && (
            <button
              className="add-array-element-button"
              onClick={() => this._handleAddArrayElement(field)}
            >
              + Add New Entry
            </button>
          )}
        </div>
      );
    });
  }

  _renderParamView(inputField, inputCurrentType, inputCurrentValue, index) {
    const {
      transformData,
      onTransformChanged,
      viewClassName,
      colors,
      allTransforms,
      allowedTransforms,
      ...otherProps
    } = this.props;
    const { name: fieldName, typesAllowed, inputElement } = inputField;

    // no type selector needed for object keys
    let shouldRenderTypeSelector =
      typesAllowed.length > 1 && !(inputElement === 'object' && index % 2 === 0);

    if (shouldRenderTypeSelector === false) {
      //generate the type supported.
      //check if inputValue is Nil && if input field has defaults for this type.
      let typeToUse =
        isNil(inputCurrentType) && typesAllowed && typesAllowed.length === 1
          ? typesAllowed[0]
          : inputCurrentType;
      let valueToDisplay =
        isNil(inputCurrentValue) && has(inputField, `default[${typeToUse}]`)
          ? `${inputField.default[typeToUse]}`
          : inputCurrentValue;

      let singleInputView = this._renderInputElement(
        inputElement,
        inputField,
        valueToDisplay,
        inputCurrentType,
        index,
      );
      return (
        <div
          key={index}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-sm)',
          }}
        >
          {singleInputView}
        </div>
      );
    }

    let paramInputView = null;
    let flexStyle = 'row';

    switch (inputCurrentType) {
      case TransformParameterTypes.FUNCTION:
        {
          let newColor = mapValues(colors, (c) => c + 20);
          let newAllowedTransforms = {};
          if (has(inputField, 'allowedRules')) {
            let allowedGroups = keys(inputField.allowedRules);
            newAllowedTransforms.groupOrder = intersection(allTransforms.groupOrder, allowedGroups);
            newAllowedTransforms.groupRules = reduce(
              inputField.allowedRules,
              (acc, g, gName) => {
                let thisGroupRule = {
                  label: allTransforms.groupRules[gName].label,
                  optionsOrder: g.optionsOrder,
                };
                acc[gName] = thisGroupRule;
                return acc;
              },
              {},
            );
          } else {
            newAllowedTransforms = allTransforms;
          }

          // let newAllowedTransforms = has(inputField, 'allowedRules')
          //   ? inputField.allowedRules
          //   : allTransforms;

          flexStyle = 'column';
          const fieldTransformData = transformData.parameters[fieldName];

          paramInputView = (
            <div className={viewClassName}>
              <TransformView
                level={this.props.level}
                transformData={
                  isArray(fieldTransformData) ? fieldTransformData[index] : fieldTransformData
                }
                onTransformChanged={this._handleParamsTransformChanged.bind(this, fieldName, index)}
                viewClassName={viewClassName}
                colors={newColor}
                allTransforms={allTransforms}
                allowedTransforms={newAllowedTransforms}
                {...otherProps}
              />
            </div>
          );
        }
        break;

      case TransformParameterTypes.SYS_ATTR_REF:
      case TransformParameterTypes.ATTR_REF:
        {
          let isMetaData = inputCurrentType === TransformParameterTypes.SYS_ATTR_REF;
          let validOptions = this._getAttributesSelectionOptions(isMetaData);
          let currSelValue = getCurrentSelectedOption(validOptions, inputCurrentValue);

          const optionsComponent = isMetaData ? MetaDataAttributeOption : AttributeOption;

          paramInputView = (
            <div
              aria-label="apply-on-attribute"
              style={{ flex: 1, marginLeft: 'var(--spacing-sm)' }}
            >
              <SortedSelect
                options={validOptions}
                isClearable={false}
                isSearchable={true}
                isCreatable={true}
                backspaceRemovesValue={false}
                variant="default"
                name="selected-attribute"
                value={currSelValue}
                styles={ReactSelectOverrides}
                components={{ Option: optionsComponent, Input: SelectInput }}
                placeholder={<span>Apply on attribute</span>}
                onChange={this._onParamAttributeChanged.bind(this, fieldName, index)}
              />
            </div>
          );
        }
        break;

      case TransformParameterTypes.STRING_INP:
      case TransformParameterTypes.NUMBER_INP:
      default:
        paramInputView = this._renderInputElement(
          inputElement,
          inputField,
          inputCurrentValue,
          inputCurrentType,
          index,
        );
    }

    let paramTypeSelectValue = getCurrentSelectedOption(ALL_SELECTION_OPTIONS, inputCurrentType);

    return (
      <div
        key={index}
        style={{
          display: 'flex',
          flexDirection: flexStyle,
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-sm)',
        }}
      >
        <div style={{ width: '30%', marginLeft: 'var(--spacing-sm)' }}>
          <SortedSelect
            styles={ReactSelectOverrides}
            options={PARAM_SELECTION_OPTIONS(typesAllowed)}
            components={{ Input: SelectInput, Option: SelectOption }}
            isClearable={false}
            backspaceRemovesValue={false}
            name="select"
            value={paramTypeSelectValue}
            onChange={this._onParamTypeChanged.bind(this, fieldName, index)}
            variant="default"
          />
        </div>
        <div style={{ flex: 1 }}>{paramInputView}</div>
      </div>
    );
  }

  _renderAttrTxParamsView(thisAttrTx = null, thisField = null) {
    const { transformData } = this.props;
    const txArgs = has(thisAttrTx, 'custom_config.ui.txArgs.items')
      ? thisAttrTx.custom_config.ui.txArgs.items
      : [];
    const txValues = thisField && get(transformData, `parameters.${thisField.name}.argValues`);
    const currentArgValues = map(txArgs, (arg, index) => {
      let argValue = {
        fName: arg.name,
        ...TRANSFORM_RULES_PROTO_STR,
      };

      if (txValues && txValues[index]) {
        argValue = { ...argValue, ...txValues[index] };
      }

      return argValue;
    });

    const argViews = map(currentArgValues, (arg, idx) => {
      const { fName, type, value, name } = arg;
      let paramTypeSelectValue = getCurrentSelectedOption(ATTR_TX_PARAM_SELECTION_OPTIONS(), type);

      let paramInputView = null;
      switch (type) {
        case TransformParameterTypes.SYS_ATTR_REF:
        case TransformParameterTypes.ATTR_REF: {
          let isMetaData = type === TransformParameterTypes.SYS_ATTR_REF ? true : false;
          let validOptions = this._getAttributesSelectionOptions(isMetaData);
          let currSelValue = getCurrentSelectedOption(validOptions, name);

          const optionsComponent = isMetaData ? MetaDataAttributeOption : AttributeOption;

          paramInputView = (
            <div
              style={{
                width: '100%',
                marginLeft: 'var(--spacing-sm)',
              }}
            >
              <SortedSelect
                options={validOptions}
                isClearable={false}
                isSearchable={true}
                backspaceRemovesValue={false}
                name="selected-attribute"
                value={currSelValue}
                styles={ReactSelectOverrides}
                components={{ Option: optionsComponent, Input: SelectInput }}
                placeholder={<span>Apply on attribute</span>}
                onChange={this._onAttrTxFuncValueChanged.bind(this, thisField, idx, fName, type)}
                variant="default"
              />
            </div>
          );

          break;
        }

        case TransformParameterTypes.STRING_INP:
        case TransformParameterTypes.NUMBER_INP:
        default: {
          let textFieldValue;
          if (value !== null) {
            textFieldValue = `${value}`;
          } else {
            textFieldValue = '';
          }

          paramInputView = (
            <div
              style={{
                display: 'flex',
                width: '100%',
              }}
            >
              <StyledInput
                variant="filled"
                value={textFieldValue}
                onChange={(e) =>
                  this._onAttrTxFuncValueChanged(thisField, idx, fName, type, e.target.value)
                }
              />
            </div>
          );
        }
      }

      return (
        <div key={idx} style={{ marginTop: 'var(--spacing-md)' }}>
          <div style={styles.label}>{fName}</div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ width: '30%' }}>
              <SortedSelect
                options={ATTR_TX_PARAM_SELECTION_OPTIONS()}
                components={{ Input: SelectInput, Option: SelectOption }}
                isClearable={false}
                backspaceRemovesValue={false}
                name="select"
                styles={ReactSelectOverrides}
                value={paramTypeSelectValue}
                onChange={this._onAttrTxFuncTypeChanged.bind(this, thisField, idx, fName)}
                variant="default"
              />
            </div>
            <div style={{ width: '70%' }}>{paramInputView}</div>
          </div>
        </div>
      );
    });

    return <div>{argViews}</div>;
  }

  _renderInputElement(
    inputElementType = 'text',
    inputField,
    inputCurrentValue,
    inputCurrentType = TransformParameterTypes.STRING_INP,
    index,
  ) {
    const { transformData, isLoadingDataMaps, loadDataMaps } = this.props;
    let inputElementView = <div />;

    switch (inputElementType) {
      case 'select':
        {
          let selectionValues = [];

          let cbPayload = {
            __fName: inputField.name,
            type:
              inputCurrentType === TransformParameterTypes.NUMBER_INP
                ? inputCurrentType
                : TransformParameterTypes.STRING_INP,
          };

          if (inputField.format === 'timezone') {
            //get list of timezones fconst { transformData } = this.props;rom moment
            let zones = DateUtils.getTimeZones();
            selectionValues = zones;
          } else {
            selectionValues = inputField.values.map((val) => {
              return { label: val, value: val };
            });
          }

          let inputCurrentSelectValue = getCurrentSelectedOption(
            selectionValues,
            inputCurrentValue,
          );

          inputElementView = (
            <div
              style={{
                width: '100%',
                marginLeft: 'var(--spacing-sm)',
              }}
            >
              <SortedSelect
                options={selectionValues}
                components={{ Input: SelectInput, Option: SelectOption }}
                isClearable={false}
                backspaceRemovesValue={false}
                value={inputCurrentSelectValue}
                styles={ReactSelectOverrides}
                onChange={this._handleSelectionChanged.bind(this, cbPayload, index)}
                isSearchable={true}
                variant="default"
              />
            </div>
          );
        }
        break;

      case 'select-creatable':
        {
          let selectionValues = [];

          let cbPayload = {
            __fName: inputField.name,
            type:
              inputCurrentType === TransformParameterTypes.NUMBER_INP
                ? inputCurrentType
                : TransformParameterTypes.STRING_INP,
          };
          selectionValues = inputField.values.map((val) => {
            return { label: val, value: val };
          });

          if (inputCurrentValue && !inputField.values?.includes(inputCurrentValue)) {
            selectionValues = [
              { label: inputCurrentValue, value: inputCurrentValue },
              ...selectionValues,
            ];
          }

          let inputCurrentSelectValue = getCurrentSelectedOption(
            selectionValues,
            inputCurrentValue,
          );

          inputElementView = (
            <div
              style={{
                width: '100%',
                marginLeft: 'var(--spacing-sm)',
              }}
            >
              <SortedSelect
                isCreatable={true}
                placeholder="Type to select or create..."
                options={selectionValues}
                components={{ Input: SelectInput, Option: SelectOption }}
                isClearable={false}
                isSearchable={true}
                backspaceRemovesValue={false}
                name="select"
                styles={ReactSelectOverrides}
                value={inputCurrentSelectValue}
                onChange={this._handleSelectionChanged.bind(this, cbPayload, index)}
                variant="default"
              />
            </div>
          );
        }

        break;

      case 'boolean':
        {
          let checkboxStatus =
            inputCurrentValue !== null
              ? !!inputCurrentValue
              : has(inputField, 'default_value')
                ? inputField.default_value
                : false;

          inputElementView = (
            <Checkbox
              key={'checkbox' + inputField.name}
              checked={checkboxStatus}
              icon={<CheckBoxOutlineBlankIcon fontSize="large" htmlColor="#D4DEDF" />}
              checkedIcon={<CheckBoxIcon fontSize="large" htmlColor="#D4DEDF" />}
              onChange={(event, isChecked) => {
                event.preventDefault();
                event.stopPropagation();
                this._handleInputCheckboxChanged(
                  inputField.name,
                  {
                    ...TRANSFORM_RULES_PROTO_STR,
                    value: isChecked,
                  },
                  index,
                );
              }}
            />
          );
        }
        break;

      case 'lookup':
      case 'lookup-nosecondary': {
        //get lookup tables list
        let dataMaps = !isEmpty(this.props.dataMaps) ? this.props.dataMaps : null;

        let currentMapValue = inputCurrentValue;
        if (isString(currentMapValue)) {
          //need datamap id
          currentMapValue = toNumber(currentMapValue);
        }

        let cbSecondaryKeyPayload = {
          __fName: 'subKey',
          type: TransformParameterTypes.STRING_INP,
        };

        let { currentValue: subKey } = getCurrentParamValue(transformData.parameters?.subKey);
        let dataMapSecondaryKeys;
        const dataMapOptions = map(dataMaps, (o) => ({
          label: `${o.name} (ID: ${o.id})`,
          value: o.id,
          id: o.id,
          description: o.description,
          dataMap: o,
        }));

        let dm = getCurrentSelectedOption(dataMapOptions, currentMapValue, null, 'id');

        let selectedSubKey = null;
        if (
          inputElementType === 'lookup' &&
          dm &&
          dm.dataMap.secondaryProperties &&
          dm.dataMap.secondaryProperties.length
        ) {
          dataMapSecondaryKeys = dm.dataMap.secondaryProperties.map((p) => {
            return { key: p, value: p, label: p };
          });
          selectedSubKey = getCurrentSelectedOption(dataMapSecondaryKeys, subKey);
        }

        inputElementView = (
          <div
            style={{
              width: '100%',
              marginLeft: 'var(--spacing-sm)',
              display: 'flex',
            }}
          >
            <SortedSelect
              className="transform-selector transform-selector--grow"
              options={dataMapOptions}
              styles={ReactSelectOverrides}
              components={{ Input: SelectInput, Option: SelectOption }}
              isClearable={false}
              backspaceRemovesValue={false}
              name="mapSelect"
              value={dm}
              onChange={this._onLookupSelectionChanged.bind(this, inputField, index)}
              variant="default"
            />

            {inputElementType === 'lookup' &&
              dataMapSecondaryKeys &&
              dataMapSecondaryKeys.length > 1 && (
                <div style={{ marginTop: 'var(--spacing-md)' }}>
                  <div style={styles.label}>Secondary Key *</div>
                  <SortedSelect
                    styles={ReactSelectOverrides}
                    options={dataMapSecondaryKeys}
                    components={{ Input: SelectInput, Option: SelectOption }}
                    isClearable={false}
                    backspaceRemovesValue={false}
                    name="secondaryKeySelect"
                    value={selectedSubKey}
                    onChange={this._handleSelectionChanged.bind(this, cbSecondaryKeyPayload, index)}
                    isSearchable={true}
                    variant="default"
                  />
                </div>
              )}
            {loadDataMaps &&
              (isLoadingDataMaps ? (
                <RefreshIndicator size={20} />
              ) : (
                <RefreshIcon
                  fontSize="large"
                  style={{
                    width: 28,
                    height: 28,
                    marginLeft: 'var(--spacing-sm)',
                    cursor: 'pointer',
                    fill: '#aaa',
                  }}
                  onClick={loadDataMaps}
                />
              ))}
          </div>
        );

        break;
      }

      case 'attrTxSelector': {
        //get lookup tables list

        let allAttrTx = !isEmpty(this.props.attrTransforms) ? this.props.attrTransforms : null;
        let currentAttrTxValue =
          this.props.selectedTransform && this.props.selectedTransform.attrTxId;
        let thisTx = has(allAttrTx, currentAttrTxValue) ? allAttrTx[currentAttrTxValue] : null;

        let codeView = null;
        let txArgsView = null;

        if (thisTx) {
          let codeLanguage = thisTx.code_type;
          codeView = (
            <div style={styles.monacoCodeView}>
              <LoadableMonacoEditor
                language={Constants.EditorModeByLanguage[codeLanguage].name}
                value={thisTx.decoded}
                height={200}
                width="100%"
                theme="light"
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                }}
              />
            </div>
          );

          txArgsView = this._renderAttrTxParamsView(thisTx, inputField);
        }

        inputElementView = (
          <div style={styles.monacoFieldView}>
            {txArgsView}
            {codeView}
          </div>
        );

        break;
      }
      case 'text':
      default: {
        let textFieldValue;
        if (inputCurrentValue !== null) {
          textFieldValue = `${inputCurrentValue}`;
        } else if (has(inputField, `default[${inputCurrentType}]`)) {
          textFieldValue = `${inputField.default[inputCurrentType]}`;
        } else {
          textFieldValue = '';
        }

        let inputCurrentData =
          inputCurrentType === TransformParameterTypes.NUMBER_INP
            ? { ...TRANSFORM_RULES_PROTO_NUM }
            : { ...TRANSFORM_RULES_PROTO_STR };

        inputElementView = (
          <div style={styles.textFieldView}>
            <TextField
              value={textFieldValue}
              fullWidth
              onChange={(event) => {
                this._handleInputChanged(
                  inputField.name,
                  {
                    ...inputCurrentData,
                    value: event.target.value,
                  },
                  index,
                );
              }}
            />
          </div>
        );

        break;
      }
    }

    return inputElementView;
  }

  _renderSrcAttrSelector(ignoreSrc = false) {
    const {
      transformData,
      onTransformChanged,
      viewClassName,
      colors,
      allTransforms,
      allowedTransforms,
      ...otherProps
    } = this.props;

    if (ignoreSrc) {
      return null;
    }

    let srcType = has(transformData, 'parameters.__srcAttr.type')
      ? transformData.parameters.__srcAttr.type
      : null;
    let srcView = null;
    let flexStyle = 'row';
    let srcParam = has(this.props, 'selectedTransform.__srcAttr')
      ? this.props.selectedTransform.__srcAttr
      : null;
    let srcLabel = srcParam ? srcParam.display_name : 'Result of *';
    let typesAllowed = has(srcParam, 'typesAllowed') ? srcParam.typesAllowed : null;
    switch (srcType) {
      case TransformParameterTypes.FUNCTION:
        {
          let newColor = mapValues(colors, (c) => c + 20);
          let newAllowedTransforms = {};
          if (has(srcParam, 'allowedRules')) {
            let allowedGroups = keys(srcParam.allowedRules);
            newAllowedTransforms.groupOrder = intersection(allTransforms.groupOrder, allowedGroups);
            newAllowedTransforms.groupRules = reduce(
              srcParam.allowedRules,
              (acc, g, gName) => {
                acc[gName] = {
                  label: allTransforms.groupRules[gName].label,
                  optionsOrder: g.optionsOrder,
                };
                return acc;
              },
              {},
            );
          } else {
            newAllowedTransforms = allTransforms;
          }

          flexStyle = 'column';

          srcView = (
            <div className={viewClassName}>
              <TransformView
                level={this.props.level}
                transformData={transformData.parameters.__srcAttr}
                onTransformChanged={this._handleSrcTransformChanged}
                viewClassName={viewClassName}
                colors={newColor}
                allTransforms={allTransforms}
                allowedTransforms={newAllowedTransforms}
                {...otherProps}
              />
            </div>
          );
        }
        break;
      case TransformParameterTypes.SYS_ATTR_REF:
      case TransformParameterTypes.ATTR_REF:
      default:
        {
          let currentSrcAttr = has(transformData, 'parameters.__srcAttr.name')
            ? transformData.parameters.__srcAttr.name
            : transformData.parameters.__srcAttr;
          let isMetaData = srcType === TransformParameterTypes.SYS_ATTR_REF;

          let validOptions = this._getAttributesSelectionOptions(isMetaData);
          let currSelValue = getCurrentSelectedOption(
            validOptions,
            currentSrcAttr,
            null,
            null,
            true,
          );

          const optionsComponent = isMetaData ? MetaDataAttributeOption : AttributeOption;

          srcView = (
            <div
              style={{
                width: '100%',
                marginLeft: 'var(--spacing-sm)',
              }}
            >
              <SortedSelect
                options={validOptions}
                components={{ Option: optionsComponent, Input: SelectInput }}
                isClearable={false}
                isSearchable={true}
                isCreatable={true}
                backspaceRemovesValue={false}
                name="selected-attribute"
                value={currSelValue}
                styles={ReactSelectOverrides}
                placeholder="Apply on attribute"
                onChange={this._onAttributeChanged}
                variant="default"
              />
            </div>
          );
        }
        break;
    }

    let srcTypeSelectValue = getCurrentSelectedOption(ALL_SELECTION_OPTIONS, srcType);

    return (
      <div aria-label="src-selector-view" className={s.container}>
        <div className={s.input_label}>{srcLabel}</div>

        <div className={s.sorted_container} style={{ flexDirection: flexStyle }}>
          <div style={{ width: '30%' }}>
            <SortedSelect
              styles={ReactSelectOverrides}
              options={SRC_SELECTION_OPTIONS(typesAllowed)}
              components={{ Input: SelectInput, Option: SelectOption }}
              isClearable={false}
              backspaceRemovesValue={false}
              name="select"
              value={srcTypeSelectValue}
              onChange={this._onSrcTypeChanged}
              variant="default"
            />
          </div>
          {srcView}
        </div>
      </div>
    );
  }

  _getAttributesFilterOptions(options, filterValue) {
    //first filter only attribute/metaData names and keep  sections
    var filteredOptionsAndSections = options.filter((option) => {
      if (!filterValue) return true;

      const labelTest = String(option.label).toLowerCase();

      return (
        labelTest.indexOf(filterValue.toLowerCase()) >= 0 ||
        (has(option, 'isSectionHeader') && option.isSectionHeader === true)
      );
    });
    //now remove all sections which don't have any filtered values.
    var onlyValidSectionsAndItems = filteredOptionsAndSections.filter((item) => {
      if (!has(item, 'isSectionHeader') || item.isSectionHeader === false) {
        return true;
      }

      let sectionHasValidItems = item.isMetaDataGroup
        ? some(filteredOptionsAndSections, (op) => {
            return op.isMetaData === true;
          })
        : some(filteredOptionsAndSections, (op) => {
            return op.isMetaData === false;
          });

      return sectionHasValidItems;
    });

    return onlyValidSectionsAndItems;
  }

  _getAttributesSelectionOptions(isMetaData = false) {
    const { allAttributes, metaDataSpec } = this.props;
    let displayOptions = [];

    //First create Meta Data Section Header
    if (isMetaData === true) {
      forEach(metaDataSpec, (metaData, metaDataName) => {
        let option = _getMetaDataSelectionItem(metaData, metaDataName);
        displayOptions.push(option);
      });
    } else {
      displayOptions = map(
        filter(allAttributes, (attr) => !attr.__controlledBy),
        (attr) => _getAttributeSelectionItem(attr),
      );
    }

    return displayOptions;
  }

  // @ waiting user's feedback to enable the vertical lines behavior
  // handleOnMouseToDotsBehavior = (enter = true) => {
  //   const { level } = this.props;
  //   for (let i = 0; i <= level; i++) {
  //     const dots = document.querySelectorAll(`div[aria-label="level-${i}"]`);
  //     dots.forEach((dot) => {
  //       if ('style' in dot) {
  //         dot.style.visibility = enter ? 'hidden' : 'visible';
  //       }
  //     });
  //   }
  // };

  render() {
    const { level } = this.props;
    let paramsListView = has(this.props.selectedTransform, 'params')
      ? this._renderParamsList()
      : null;

    let ignoreSrc = has(this.props.selectedTransform, 'ignoreSrc')
      ? this.props.selectedTransform.ignoreSrc
      : false;

    let srcSelectorView = this._renderSrcAttrSelector(ignoreSrc);

    return (
      <div
        aria-label="transform-parameters"
        className={cx(s.transform_hover_action, s.dots_container)}
      >
        <div style={{ width: '100%', paddingLeft: 'var(--spacing-sm)' }}>
          <TransformParametersDotList level={level} className={s.indentation_dots} />

          {srcSelectorView}
          {paramsListView ? <div aria-label="params-list-view">{paramsListView}</div> : null}
        </div>
      </div>
    );
  }
}

export default TransformParameters;
